import axios from "axios";
import { resolveLocalServerApiBase } from "../apiConfig";
import { attachLocalServerAuthHeaders } from "../localServerAuth";

// Create axios instance for folder structure API
const folderApi = axios.create({
  baseURL: resolveLocalServerApiBase(),
  headers: {
    "Content-Type": "application/json",
  },
});
folderApi.interceptors.request.use(attachLocalServerAuthHeaders);

const getClinicsAndFolders = (state) => {
    const paths = !state.currentFolderPath
    ? []
    : state.currentFolderPath.split("/");
  console.log(
    "paths",
    paths.reduce((acc, path) => {
      return acc.find((item) => item.folderName === path)?.items || [];
    }, state.folderStructure?.items || [])
  );
  return paths.reduce((acc, path) => {
    return (
      acc.find((item) => item?.data?.folderName === path)?.data?.items || []
    );
  }, state.folderStructure?.items || []);
};

const getFolders = (state) => {
    return getClinicsAndFolders(state).filter((i)=>i.type === "folder");
}

export const getClinicsInFolder =  (folderData) => {
    if(folderData.type === "folder"){
        return folderData.data.items.map((item)=>{
            return item.type === "clinic" ? item : getClinicsInFolder(item);
        }).flat();
    }
    return [];
};
const getCurrentFolder = (state) => {
  if (!state.currentFolderPath) return state.folderStructure ?? null;
  const paths = !state.currentFolderPath
    ? []
    : state.currentFolderPath.split("/");
  return paths.reduce((acc, path) => {
    return acc.items.find((item) => item?.data?.folderName === path)?.data || [];
  }, state.folderStructure || []);
};

export default {
  namespaced: true,

  state: {
    folderStructure: null,
    loading: false,
    error: null,
    currentFolder: null,
    currentFolderPath: null,
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },

    SET_ERROR(state, error) {
      state.error = error;
    },

    SET_FOLDER_STRUCTURE(state, structure) {
      state.folderStructure = structure;
    },

    SET_CURRENT_FOLDER(state, folder) {
      state.currentFolder = folder;
    },

    CLEAR_ERROR(state) {
      state.error = null;
    },
    SET_CURRENT_FOLDER_PATH(state, path) {
      state.currentFolderPath = path;
    },
  },

  actions: {
    // Fetch complete folder structure
    async fetchFolderStructure({ commit }) {
      try {
        commit("SET_LOADING", true);
        commit("CLEAR_ERROR");

        const adminId = localStorage.getItem("link");
        const response = await folderApi.get(
          `/userrecord/admin/clinic/folder/${adminId}`
        );
        commit("SET_FOLDER_STRUCTURE", response.data);

        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to fetch folder structure";
        commit("SET_ERROR", errorMessage);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Create new folder
    async createFolder({ commit }, { folderName, parentFolderId = null }) {
      try {
        commit("SET_LOADING", true);
        commit("CLEAR_ERROR");

        const response = await folderApi.post("/folders/create", {
          folderName,
          parentFolderId,
        });

        // Refresh folder structure after creation
        await this.dispatch("folderClinic/fetchFolderStructure");

        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to create folder";
        commit("SET_ERROR", errorMessage);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Rename existing folder
    async renameFolder({ commit }, { folderId, folderName }) {
      try {
        commit("SET_LOADING", true);
        commit("CLEAR_ERROR");

        const response = await folderApi.put("/folders/rename", {
          folderId,
          folderName,
        });

        // Refresh folder structure after rename
        await this.dispatch("folderClinic/fetchFolderStructure");

        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to rename folder";
        commit("SET_ERROR", errorMessage);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Delete folder and contents
    async deleteFolder({ commit }, folderId) {
      try {
        commit("SET_LOADING", true);
        commit("CLEAR_ERROR");

        await folderApi.delete(`/folders/delete/${folderId}`);

        // Refresh folder structure after deletion
        await this.dispatch("folderClinic/fetchFolderStructure");

        return true;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to delete folder";
        commit("SET_ERROR", errorMessage);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Add clinics to folder
    async addClinicsToFolder({commit}, payload) {
      console.log("addClinicsToFolder called with:", payload);
      try {
        commit("SET_LOADING", true);
        commit("CLEAR_ERROR");

        const rawFolderId = payload?.folderId;
        const folderId =
          rawFolderId === null || rawFolderId === undefined || rawFolderId === ""
            ? null
            : Number(rawFolderId);
        if (rawFolderId != null && rawFolderId !== "" && Number.isNaN(folderId)) {
          throw new Error(`Invalid folderId (expected number): ${rawFolderId}`);
        }
        const clinicLinkIds = (Array.isArray(payload?.clinicLinkIds)
          ? payload.clinicLinkIds
          : []
        )
          .map((id) => (id == null ? "" : String(id).trim()))
          .filter(Boolean);
        const clinicNames = Array.isArray(payload?.clinicNames)
          ? payload.clinicNames.map((n, i) => n || clinicLinkIds[i] || "")
          : clinicLinkIds;

        if (!clinicLinkIds.length) {
          throw new Error("No clinics provided to add");
        }

        const response = await folderApi.post("/folders/add-clinics", {
          folderId,
          clinicLinkIds,
          clinicNames,
        });

        // Refresh folder structure after adding clinics
        await this.dispatch("folderClinic/fetchFolderStructure");

        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || error.message || "Failed to add clinics to folder";
        commit("SET_ERROR", errorMessage);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Move folders to parent
    async moveFoldersToParent({ commit }, { folderIds, parentFolderId }) {
      try {
        commit("SET_LOADING", true);
        commit("CLEAR_ERROR");

        const response = await folderApi.post("/folders/add-folders", {
          folderIds,
          parentFolderId,
        });

        // Refresh folder structure after moving folders
        await this.dispatch("folderClinic/fetchFolderStructure");

        return response.data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to move folders";
        commit("SET_ERROR", errorMessage);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },
    //  async deleteClinic({ commit }, clinicLinkId) {
        async deleteClinic({ commit }, { clinicLinkId, folderId }) {
       try {
         commit("SET_LOADING", true);
         commit("CLEAR_ERROR");

        await folderApi.delete(
          `/folders/delete-clinic/${clinicLinkId}?folderId=${folderId}`
        );

         // Refresh folder structure after deletion
         await this.dispatch("folderClinic/fetchFolderStructure");

         return true;
       } catch (error) {
         const errorMessage =
           error.response?.data?.message || "Failed to delete clinic";
         commit("SET_ERROR", errorMessage);
         throw error;
       } finally {
         commit("SET_LOADING", false);
       }
     },
  },

  getters: {
    loading: (state) => state.loading,
    error: (state) => state.error,
    currentFolder: (state) => state.currentFolder,
    folderStructure: (state) => state.folderStructure,
    getCurrentFolderPath: (state) => state.currentFolderPath,

    getCategoriesViewFolders: (state) => {
  // helper function inside the getter
  const getTotalClinics = (folder) => {
    if (!folder?.data?.items) return 0;
    let total = 0;
    for (const item of folder.data.items) {
      if (item.type === "clinic") total++;
      else if (item.type === "folder") total += getTotalClinics(item);
    }
    return total;
  };

  const folders = getFolders(state).map((folder) => {
    const directClinics = (folder.data.items || []).filter(i => i.type === "clinic");
    const totalClinics = getTotalClinics(folder); // total including subfolders
    const isFolderDisabled = !folder.data.items || folder.data.items.filter(i => i.type === "folder").length === 0;

    return {
      ...folder,
      clinics: directClinics, // only direct clinics for table
      totalClinics,           // total clinics including subfolders for display
      disabled: isFolderDisabled
    };
  });

  return folders;
},



getFolderByPath: (state) => (path) => {
  if (!state.folderStructure || !path) return null;
  const paths = path.split("/");
  const findFolder = (folders, pathArr) => {
    if (!Array.isArray(folders) || pathArr.length === 0) return null;
    for (const folder of folders) {
      if (folder.data?.folderName === pathArr[0]) {
        if (pathArr.length === 1) return folder;
        const subFolders = (folder.data.items || []).filter(i => i.type === "folder");
        return findFolder(subFolders, pathArr.slice(1));
      }
    }
    return null;
  };
  return findFolder(state.folderStructure.items || [], paths);
},

// Count only direct clinics in a folder
getDirectClinicCount: () => (folder) => {
  // Only direct clinics inside folder
  return (folder.data.items || []).filter(i => i.type === "clinic").length;
},

getTotalClinicCountIncludingSubfolders: () => (folder) => {
  const countClinics = (items) => {
    let total = 0;
    items.forEach(i => {
      if (i.type === "clinic") total++;
      else if (i.type === "folder") total += countClinics(i.data.items || []);
    });
    return total;
  };
  return countClinics(folder.data.items || []);
},


    // Get all folders from the structure (recursive)
    allFolders: (state) => {
      if (!state.folderStructure) return [];

      const extractFolders = (items) => {
        let folders = [];
        items.forEach((item) => {
          if (item.type === "folder") {
            folders.push(item.data);
            if (item.items && item.items.length > 0) {
              folders = folders.concat(extractFolders(item.items));
            }
          }
        });
        return folders;
      };

      return extractFolders(state.folderStructure.items || []);
    },

    // Get folders by parent ID
    foldersByParent: (state) => (parentId) => {
      if (!state.folderStructure) return [];

      if (parentId === null) {
        return state.folderStructure.items || [];
      }
    },

    // Get root folders (no parent)
    rootFolders: (state) => {
      if (!state.folderStructure) return [];

      const findRootFolders = (items) => {
        let folders = [];
        items.forEach((item) => {
          if (item.type === "folder" && !item.data.parentFolderId) {
            folders.push(item.data);
          }
          if (item.items && item.items.length > 0) {
            folders = folders.concat(findRootFolders(item.items));
          }
        });
        return folders;
      };

      return findRootFolders(state.folderStructure.items || []);
    },

    // Get folder by ID
    getFolderById: (state) => (id) => {
      if (!state.folderStructure) return null;

      const findFolderById = (items, targetId) => {
        for (let item of items) {
          if (item.type === "folder" && item.data.folderId === targetId) {
            return item.data;
          }
          if (item.items && item.items.length > 0) {
            const found = findFolderById(item.items, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      return findFolderById(state.folderStructure.items || [], id);
    },

    // Get clinics in a specific folder
    getClinicsInFolder: (state) => (folderId) => {
      if (!state.folderStructure) return [];

      const findClinicsInFolder = (items, targetFolderId) => {
        let clinics = [];
        items.forEach((item) => {
          if (item.type === "folder" && item.data.folderId === targetFolderId) {
            // Find clinics in this folder
            item.items.forEach((subItem) => {
              if (subItem.type === "clinic") {
                clinics.push(subItem.data);
              }
            });
          }
          if (item.items && item.items.length > 0) {
            clinics = clinics.concat(
              findClinicsInFolder(item.items, targetFolderId)
            );
          }
        });
        return clinics;
      };

      return findClinicsInFolder(state.folderStructure.items || [], folderId);
    },

    getCurrentClinicsAndFolders: (state) => {
      return getClinicsAndFolders(state);
    },
    // getCategoriesViewFolders: (state) => {
    //   const folders = getFolders(state).map((i)=>{
    //     const isFolderDisabled = !i.data.items || i.data.items.filter((i)=>i.type === "folder").length === 0;
    //     return(
    //         {
    //             ...i,
    //             clinics: getClinicsInFolder(i),
    //             disabled: isFolderDisabled
    //         }
    //     )
    //   })
      // if(!state.currentFolderPath){
      //   return [...folders, {
      //       type: "folder",
      //       data: {
      //           folderName: "Uncategorized",
      //           folderId: null,
      //           parentFolderId: null,
      //           items: state.folderStructure?.items.filter((i)=>i.type === "clinic") || []
      //       },
      //       clinics: state.folderStructure?.items.filter((i)=>i.type === "clinic") || [],
      //       disabled: true
      //   }];
      // }
    //   return folders;
    // },
    
    getCurrentFolder: getCurrentFolder
  },
};
